import { motion } from 'framer-motion';
import type { AiMessage } from '@vtak/shared';
import { cn } from '@/lib/utils';
import { MarkdownContent } from './markdown-content';

interface AiMessageBubbleProps {
  message: AiMessage;
}

/** Single message bubble for AI conversations */
export function AiMessageBubble({ message }: AiMessageBubbleProps): React.JSX.Element {
  const isAssistant = message.role === 'assistant';
  const time = formatTime(message.createdAt);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={cn(
        'rounded-md bg-white/[0.04] px-3 py-2 text-sm',
        isAssistant && 'border-l-[3px] border-l-ai bg-ai/[0.08]',
      )}
    >
      <div className="mb-0.5 flex items-center justify-between">
        <span
          className={cn(
            'font-chakra text-[10px] font-bold uppercase tracking-widest',
            isAssistant ? 'text-ai' : 'text-primary',
          )}
        >
          {isAssistant ? 'VTAK AI' : 'You'}
        </span>
        <span className="font-mono text-[9px] tracking-wide text-muted-foreground/70">{time}</span>
      </div>
      {isAssistant ? (
        <MarkdownContent content={message.content} />
      ) : (
        <p className="text-foreground">{message.content}</p>
      )}
    </motion.div>
  );
}

function formatTime(isoString: string): string {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
